import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MessageCircle, ArrowRight, Camera, Car, Bell, Globe, FileCheck2, Zap } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import WhatsAppDemo from '../components/WhatsAppDemo';
import { WHATSAPP_LINK } from '../utils/constants';
import { WB_CONTENT } from '../data/whatsappBookkeepingContent';

const STEPS = [
  { n: '1', title: 'Text it on WhatsApp', desc: 'Send "earned $500 from a client" or "spent 40 on fuel" — in any language.' },
  { n: '2', title: 'We categorize it', desc: 'AI reads your message, logs the income or expense and files it under the right tax category.' },
  { n: '3', title: 'Get tax-ready reports', desc: 'Your dashboard, P&L and tax summary update instantly — export whenever you need.' },
];

const FEATURES = [
  { icon: Camera, title: 'Snap receipts', desc: 'Send a photo and we extract the amount, date and merchant automatically.' },
  { icon: Car, title: 'Log mileage', desc: 'Text "drove 22 miles to a client" for an IRS/HMRC-ready mileage log.' },
  { icon: Bell, title: 'Smart reminders', desc: 'Bill due dates and quarterly tax deadlines, straight to your chat.' },
  { icon: Globe, title: 'Multi-language', desc: 'Works in English, Spanish, Arabic, Hindi and more — just type naturally.' },
  { icon: FileCheck2, title: 'Tax-ready exports', desc: 'Schedule C, Self Assessment, T2125 and VAT summaries in a click.' },
  { icon: Zap, title: 'Nothing to install', desc: 'No new app to learn. If you can text, you can do your books.' },
];

const FAQS = WB_CONTENT.faqs;

export default function WhatsAppBookkeeping() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'WhatsApp Bookkeeping for Freelancers',
      url: 'https://moneyfeed.io/whatsapp-bookkeeping',
      description: 'Track expenses, income, mileage and taxes by texting on WhatsApp. AI bookkeeping for freelancers and the self-employed.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#E0F2FE] to-[#D1FAE5]" data-testid="whatsapp-bookkeeping-page">
      <Seo
        title="WhatsApp Bookkeeping — Do Your Books by Text | MoneyFeed"
        description="Do your bookkeeping on WhatsApp. Text income & expenses, snap receipts, log mileage and get tax-ready reports — for the US, UK, Canada, UAE & Saudi."
        path="/whatsapp-bookkeeping"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-8 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#25D366]/10 rounded-full mb-5">
            <MessageCircle className="h-4 w-4 text-[#128C7E]" />
            <span className="text-xs font-bold text-[#128C7E] uppercase tracking-wider">Bookkeeping on WhatsApp</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-4">
            Do your bookkeeping by <span className="text-[#25D366]">texting</span> on WhatsApp
          </h1>
          <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-xl">
            Just message your income and expenses like you'd text a friend. MoneyFeed's <Link to="/ai-bookkeeping" className="text-[#128C7E] underline hover:text-[#064E3B]">AI categorizes</Link> everything and keeps you tax-ready — no spreadsheets, no new app to learn. It's the bookkeeping side of <Link to="/whatsapp-accounting" className="text-[#128C7E] underline hover:text-[#064E3B]">WhatsApp accounting</Link> — with automatic <Link to="/expense-tracking" className="text-[#128C7E] underline hover:text-[#064E3B]">expense tracking</Link> built in.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/auth">
              <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="wb-get-started">
                Get started free <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="wb-whatsapp">
                <MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp
              </Button>
            </a>
          </div>
        </div>
        <div className="flex justify-center" data-testid="wb-demo">
          <WhatsAppDemo />
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">How WhatsApp bookkeeping works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl shadow-md p-6" data-testid={`wb-step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-[#064E3B] text-white font-bold flex items-center justify-center mb-4">{s.n}</div>
              <h3 className="text-lg font-bold text-[#064E3B] mb-2">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Educational content + prominent WhatsApp CTA (shared with prerender) */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12" data-testid="wb-edu">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-8">What is WhatsApp bookkeeping?</h2>
        {WB_CONTENT.sections.map((s) => (
          <div key={s.i} className="mb-6">
            <h3 className="text-lg font-bold text-[#064E3B] mb-2">{s.h}</h3>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed">{WB_CONTENT.paragraphs[s.i]}</p>
          </div>
        ))}
        <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6">
          WhatsApp bookkeeping is the capture side of full <Link to="/whatsapp-accounting" className="text-[#128C7E] underline hover:text-[#064E3B]">WhatsApp accounting</Link> — the books, profit &amp; loss and tax reports build themselves from what you send.
        </p>
        <div className="text-center">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white font-bold rounded-full px-8 py-6 text-base" data-testid="wb-edu-cta">
              <MessageCircle className="h-5 w-5 mr-2" /> Start Bookkeeping on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Everything you can do from a chat</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl shadow-md p-6 flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                <f.icon className="h-5 w-5 text-[#064E3B]" />
              </div>
              <div>
                <h3 className="font-bold text-[#064E3B] mb-1">{f.title}</h3>
                <p className="text-gray-600 text-sm">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Frequently asked questions</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`wb-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Start tracking in the next 30 seconds</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">No spreadsheets. No accounting jargon. Just text your first expense and watch MoneyFeed do the rest.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth">
              <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="wb-cta-signup">
                Get started free <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/tools/1099-tax-calculator">
              <Button variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base" data-testid="wb-cta-calc">
                Try the free tax calculator
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-accounting" className="hover:text-[#064E3B]">WhatsApp Accounting</Link>
          <Link to="/ai-bookkeeping" className="hover:text-[#064E3B]">AI Bookkeeping</Link>
          <Link to="/expense-tracking" className="hover:text-[#064E3B]">Expense Tracking</Link>
          <Link to="/mileage-tracking" className="hover:text-[#064E3B]">Mileage Tracking</Link>
          <Link to="/blog" className="hover:text-[#064E3B]">Blog</Link>
          <Link to="/tools/1099-tax-calculator" className="hover:text-[#064E3B]">Tax Calculator</Link>
          <Link to="/auth" className="hover:text-[#064E3B]">Sign in</Link>
        </div>
      </footer>
    </div>
  );
}
